import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth, useTheme, useLanguage, useConfirm } from '../../context/AppContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import SenArtisteLogo from '../../components/SenArtisteLogo';
import LanguageSelector from '../../components/LanguageSelector';
import { 
  Sun, Moon, Users, Music, Ticket, DollarSign, Plus, Edit, Trash2, 
  Eye, EyeOff, Power, Search, LogOut, LayoutDashboard, Megaphone,
  Upload, ImageIcon, Loader2, UserPlus, QrCode, Copy, ExternalLink, Settings, CreditCard, Percent, Key, AlertTriangle, Calendar,
  Bell, CheckCircle, Clock, Banknote, ArrowRight, Phone, Check, X as XIcon, Star, MessageCircleQuestion, Filter
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, api, loading: authLoading } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t, language } = useLanguage();
  const confirm = useConfirm();
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({});
  const [artists, setArtists] = useState([]);
  const [globalBanners, setGlobalBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Incidents state
  const [incidents, setIncidents] = useState([]);
  const [incidentsStats, setIncidentsStats] = useState({ new: 0, inProgress: 0 });
  const [incidentsLoading, setIncidentsLoading] = useState(false);
  const [incidentsPage, setIncidentsPage] = useState(1);
  const [incidentsTotalPages, setIncidentsTotalPages] = useState(1);
  const [incidentsFilter, setIncidentsFilter] = useState({ status: '', category: '' });
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [incidentDetailOpen, setIncidentDetailOpen] = useState(false);
  
  // Fans state
  const [fans, setFans] = useState([]);
  const [fansLoading, setFansLoading] = useState(false);
  const [fansPage, setFansPage] = useState(1);
  const [fansTotalPages, setFansTotalPages] = useState(1);
  const [fansSearch, setFansSearch] = useState('');
  const [selectedFan, setSelectedFan] = useState(null);
  const [fanDetailOpen, setFanDetailOpen] = useState(false);
  
  // Form states
  const [artistForm, setArtistForm] = useState({
    slug: '',
    prenom: '',
    nom: '',
    nomDeScene: '',
    emailContact: '',
    telephoneContact: '',
    bio: '',
    primaryColor: '#D4AF37',
    secondaryColor: '#1A1A1A',
  });
  const [bannerForm, setBannerForm] = useState({
    title: '',
    description: '',
    imageUrl: '',
    contactPhone: '',
    contactEmail: '',
    shopUrl: '',
    isActive: true,
    targetTenants: [],
  });
  
  // Apporteur form
  const [apporteurForm, setApporteurForm] = useState({
    firstName: '',
    lastName: '',
    phoneWave: '',
    phoneOrangeMoney: '',
    iban: '',
    bic: '',
    email: '',
    stripe_connect_enabled: false,
  });
  const [apporteurs, setApporteurs] = useState([]);
  const [apporteurDialogOpen, setApporteurDialogOpen] = useState(false);
  const [editingApporteur, setEditingApporteur] = useState(null);
  const [selectedApporteur, setSelectedApporteur] = useState(null);
  const [apporteurDetailDialogOpen, setApporteurDetailDialogOpen] = useState(false);
  
  // Platform config state
  const [platformConfig, setPlatformConfig] = useState({
    commission_fcfa_percent: 5,
    commission_eur_percent: 2.5,
    commission_eur_fixed: 0.25,
    apporteur_share_percent: 1.0,
    apporteur_share_percent_fcfa: 1.0,
    apporteur_share_percent_eur: 0,
    apporteur_events_limit: 5,
    artist_share_percent: 96,
    transfer_delay_days: 7,
    platform_fixed_fee_enabled: true,
    platform_fixed_fee_eur: 0.50,
    dkr_fee_percent: 1.0,
    platform_payment_number: '+221778312002',
    platform_reserve_percent: 1.0
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const [platformPayouts, setPlatformPayouts] = useState([]);
  const [platformPayoutsTotals, setPlatformPayoutsTotals] = useState({});
  
  // Pending transfers state
  const [pendingTransfers, setPendingTransfers] = useState({ ready: [], future: [], ready_count: 0, future_count: 0, ready_total: 0, future_total: 0 });
  const [executingTransfers, setExecutingTransfers] = useState(false);
  
  // Reversements state (nouveau dashboard simplifié)
  const [reversements, setReversements] = useState([]);
  const [reversementsTotals, setReversementsTotals] = useState({ eur: {}, fcfa: {} });
  const [reversementsSummary, setReversementsSummary] = useState({});
  const [markingWithdrawn, setMarkingWithdrawn] = useState(null);
  
  // Pending payouts state (reversements échoués en attente de traitement manuel)
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [pendingPayoutsSummary, setPendingPayoutsSummary] = useState({});
  const [processingPayout, setProcessingPayout] = useState(null);
  
  // Admin notifications state
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [notificationsCount, setNotificationsCount] = useState({ unread: 0, unprocessed: 0 });
  
  // Artist actions state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [selectedArtistForAction, setSelectedArtistForAction] = useState(null);
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Dialog states
  const [artistDialogOpen, setArtistDialogOpen] = useState(false);
  const [bannerDialogOpen, setBannerDialogOpen] = useState(false);
  const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  
  // Upload states
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const bannerFileInputRef = useRef(null);

  useEffect(() => {
    // Attendre que l'authentification soit chargée avant de vérifier
    if (authLoading) return;
    
    if (!user || user.role !== 'SUPER_ADMIN') {
      navigate('/login');
      return;
    }
    fetchAllData();
    fetchApporteurs();
    fetchPlatformConfig();
    fetchPendingTransfers();
    fetchReversements();
    fetchPendingPayouts();
    fetchAdminNotifications();
  }, [user, authLoading]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, artistsRes, bannersRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/artists'),
        api.get('/admin/global-banners'),
      ]);
      
      setStats(statsRes.data);
      setArtists(artistsRes.data);
      setGlobalBanners(bannersRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateArtist = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/admin/artists', artistForm);
      toast.success('Artiste créé avec succès');
      setCreatedCredentials(response.data.credentials);
      setArtistDialogOpen(false);
      setCredentialsDialogOpen(true);
      setArtistForm({
        slug: '',
        prenom: '',
        nom: '',
        nomDeScene: '',
        emailContact: '',
        telephoneContact: '',
        bio: '',
        primaryColor: '#D4AF37',
        secondaryColor: '#1A1A1A',
      });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleToggleArtistStatus = async (artistId) => {
    try {
      const response = await api.put(`/admin/artists/${artistId}/toggle-status`);
      toast.success(`Artiste ${response.data.isActive ? 'activé' : 'désactivé'}`);
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleToggleArtistPremium = async (artistId, currentStatus) => {
    try {
      const response = await api.put(`/admin/artists/${artistId}/toggle-premium`);
      toast.success(`Artiste ${response.data.isPremium ? 'passé en Premium' : 'retiré du Premium'}`);
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut Premium');
    }
  };

  const handleCreateBanner = async (e) => {
    e.preventDefault();
    if (!bannerForm.imageUrl) {
      toast.error('Veuillez uploader une image');
      return;
    }
    try {
      if (editingBanner) {
        // Mise à jour d'une bannière existante
        await api.put(`/admin/global-banners/${editingBanner.id}`, bannerForm);
        toast.success('Bannière mise à jour');
      } else {
        // Création d'une nouvelle bannière
        await api.post('/admin/global-banners', bannerForm);
        toast.success('Bannière créée');
      }
      setBannerDialogOpen(false);
      setEditingBanner(null);
      setBannerForm({ title: '', description: '', imageUrl: '', contactPhone: '', contactEmail: '', shopUrl: '', isActive: true, targetTenants: [] });
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  // État pour l'édition
  const [editingBanner, setEditingBanner] = useState(null);

  // Éditer une bannière
  const handleEditBanner = (banner) => {
    setEditingBanner(banner);
    setBannerForm({
      title: banner.title || '',
      description: banner.description || '',
      imageUrl: banner.imageUrl || '',
      contactPhone: banner.contactPhone || '',
      contactEmail: banner.contactEmail || '',
      shopUrl: banner.shopUrl || '',
      isActive: banner.isActive !== false,
      targetTenants: banner.targetTenants || []
    });
    setBannerDialogOpen(true);
  };

  // Activer/Désactiver une bannière
  const handleToggleBannerStatus = async (bannerId, isActive) => {
    try {
      await api.put(`/admin/global-banners/${bannerId}`, { isActive });
      toast.success(isActive ? 'Bannière activée' : 'Bannière désactivée');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Supprimer une bannière
  const handleDeleteBanner = async (bannerId) => {
    const confirmed = await confirm({
      title: t('deleteConfirmTitle'),
      message: t('deleteBannerDesc'),
      type: 'danger',
      confirmText: t('delete'),
      cancelText: t('cancel')
    });
    if (!confirmed) return;
    try {
      await api.delete(`/admin/global-banners/${bannerId}`);
      toast.success('Bannière supprimée');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Upload de l'image de bannière
  const handleBannerImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Vérifier le type de fichier
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez JPG, PNG, GIF ou WEBP');
      return;
    }
    
    // Vérifier la taille (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image trop volumineuse (max 10MB)');
      return;
    }
    
    setUploadingBanner(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/upload/file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Construire l'URL complète à partir de l'URL relative
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const imageUrl = response.data.url.startsWith('/') 
        ? `${baseUrl}${response.data.url}` 
        : response.data.url;
      
      setBannerForm(prev => ({ ...prev, imageUrl }));
      toast.success('Image uploadée');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploadingBanner(false);
    }
  };

  // ============== APPORTEURS ==============
  const fetchApporteurs = async () => {
    try {
      const res = await api.get('/admin/apporteurs');
      setApporteurs(res.data);
    } catch (error) {
      console.error('Error fetching apporteurs:', error);
    }
  };

  // ============== PLATFORM CONFIG ==============
  const fetchPlatformConfig = async () => {
    try {
      const res = await api.get('/admin/platform-config');
      setPlatformConfig(res.data);
    } catch (error) {
      console.error('Error fetching platform config:', error);
    }
  };

  // Fetch pending transfers
  const fetchPendingTransfers = async () => {
    try {
      const res = await api.get('/stripe/pending-transfers');
      setPendingTransfers(res.data);
    } catch (error) {
      console.error('Error fetching pending transfers:', error);
    }
  };

  // ============== REVERSEMENTS (Dashboard simplifié) ==============
  const fetchReversements = async () => {
    try {
      const res = await api.get('/admin/reversements');
      setReversements(res.data.reversements || []);
      setReversementsTotals(res.data.totals || { eur: {}, fcfa: {} });
      setReversementsSummary(res.data.summary || {});
    } catch (error) {
      console.error('Error fetching reversements:', error);
    }
    try {
      const ppRes = await api.get('/admin/platform-payouts');
      setPlatformPayouts(ppRes.data.payouts || []);
      setPlatformPayoutsTotals(ppRes.data.totals || {});
    } catch (error) {
      console.error('Error fetching platform payouts:', error);
    }
  };

  const handleMarkWithdrawn = async (eventId, currency, method = 'manual', reference = '') => {
    setMarkingWithdrawn(eventId);
    try {
      const res = await api.post(`/admin/reversements/${eventId}/mark-withdrawn`, {
        currency,
        method,
        reference
      });
      toast.success(res.data.message);
      fetchReversements();
      fetchAdminNotifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du marquage');
    } finally {
      setMarkingWithdrawn(null);
    }
  };

  // ============== PENDING PAYOUTS (Reversements échoués) ==============
  const fetchPendingPayouts = async () => {
    try {
      const res = await api.get('/admin/pending-payouts');
      setPendingPayouts(res.data.pending || []);
      setPendingPayoutsSummary(res.data.summary || {});
    } catch (error) {
      console.error('Error fetching pending payouts:', error);
    }
  };

  const handleMarkPayoutDone = async (payoutId, note = '', reference = '') => {
    setProcessingPayout(payoutId);
    try {
      const res = await api.post(`/admin/pending-payouts/${payoutId}/mark-done`, {
        note,
        reference
      });
      toast.success(res.data.message);
      fetchPendingPayouts();
      fetchAdminNotifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du traitement');
    } finally {
      setProcessingPayout(null);
    }
  };

  const handleCancelPendingPayout = async (payoutId, reason = '') => {
    setProcessingPayout(payoutId);
    try {
      const confirmed = await confirm({
        title: 'Annuler ce reversement ?',
        message: 'Les fonds seront remis à disposition de l\'artiste pour un futur reversement.',
        type: 'warning',
        confirmText: 'Annuler le reversement',
        cancelText: 'Retour'
      });
      if (!confirmed) {
        setProcessingPayout(null);
        return;
      }
      const res = await api.post(`/admin/pending-payouts/${payoutId}/cancel`, { reason });
      toast.success(res.data.message);
      fetchPendingPayouts();
      fetchAdminNotifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    } finally {
      setProcessingPayout(null);
    }
  };

  // ============== ADMIN NOTIFICATIONS ==============
  const fetchAdminNotifications = async () => {
    try {
      const res = await api.get('/admin/notifications');
      setAdminNotifications(res.data.notifications || []);
      setNotificationsCount({
        unread: res.data.unreadCount || 0,
        unprocessed: res.data.unprocessedCount || 0
      });
    } catch (error) {
      console.error('Error fetching admin notifications:', error);
    }
  };

  const handleMarkNotificationRead = async (notificationId) => {
    try {
      await api.put(`/admin/notifications/${notificationId}/read`);
      fetchAdminNotifications();
    } catch (error) {
      toast.error('Erreur lors du marquage');
    }
  };

  const handleMarkNotificationProcessed = async (notificationId, note = '') => {
    try {
      await api.put(`/admin/notifications/${notificationId}/process`, { note });
      toast.success('Notification marquée comme traitée');
      fetchAdminNotifications();
    } catch (error) {
      toast.error('Erreur lors du marquage');
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    const confirmed = await confirm({
      title: t('deleteConfirmTitle'),
      message: t('deleteNotificationDesc'),
      type: 'danger',
      confirmText: t('delete'),
      cancelText: t('cancel')
    });
    if (!confirmed) return;
    try {
      await api.delete(`/admin/notifications/${notificationId}`);
      toast.success('Notification supprimée');
      fetchAdminNotifications();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // ============== INCIDENTS MANAGEMENT ==============
  const fetchIncidents = async (page = 1, filters = incidentsFilter) => {
    setIncidentsLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (filters.status) params.append('incident_status', filters.status);
      if (filters.category) params.append('category', filters.category);
      
      const res = await api.get(`/admin/incidents?${params}`);
      setIncidents(res.data.incidents || []);
      setIncidentsTotalPages(res.data.totalPages || 1);
      setIncidentsStats(res.data.stats || { new: 0, inProgress: 0 });
    } catch (error) {
      console.error('Error fetching incidents:', error);
      toast.error('Erreur lors du chargement des incidents');
    } finally {
      setIncidentsLoading(false);
    }
  };

  const handleUpdateIncident = async (incidentId, updates) => {
    try {
      await api.put(`/admin/incidents/${incidentId}`, updates);
      toast.success('Incident mis à jour');
      fetchIncidents(incidentsPage);
      if (selectedIncident?.id === incidentId) {
        setSelectedIncident(prev => ({ ...prev, ...updates }));
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteIncident = async (incidentId) => {
    const confirmed = await confirm({
      title: 'Supprimer l\'incident',
      message: 'Êtes-vous sûr de vouloir supprimer cet incident ?',
      type: 'danger',
      confirmText: 'Supprimer',
      cancelText: 'Annuler'
    });
    if (!confirmed) return;
    try {
      await api.delete(`/admin/incidents/${incidentId}`);
      toast.success('Incident supprimé');
      fetchIncidents(incidentsPage);
      setIncidentDetailOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openIncidentDetail = async (incident) => {
    try {
      const res = await api.get(`/admin/incidents/${incident.id}`);
      setSelectedIncident(res.data.incident);
      setIncidentDetailOpen(true);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  // ============== FANS MANAGEMENT ==============
  const fetchFans = async (page = 1, search = '') => {
    setFansLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (search) params.append('search', search);
      
      const res = await api.get(`/admin/fans?${params}`);
      setFans(res.data.fans || []);
      setFansTotalPages(res.data.totalPages || 1);
    } catch (error) {
      console.error('Error fetching fans:', error);
      toast.error('Erreur lors du chargement des fans');
    } finally {
      setFansLoading(false);
    }
  };

  const openFanDetail = async (fan) => {
    try {
      const res = await api.get(`/admin/fans/${fan.id}`);
      setSelectedFan(res.data);
      setFanDetailOpen(true);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const handleResetFanPassword = async (fanId) => {
    const confirmed = await confirm({
      title: 'Réinitialiser le mot de passe',
      message: 'Un nouveau mot de passe sera généré et affiché.',
      type: 'warning',
      confirmText: 'Réinitialiser',
      cancelText: 'Annuler'
    });
    if (!confirmed) return;
    try {
      const res = await api.put(`/admin/fans/${fanId}/reset-password`);
      toast.success(`Nouveau mot de passe: ${res.data.newPassword}`, { duration: 10000 });
    } catch (error) {
      toast.error('Erreur lors de la réinitialisation');
    }
  };

  const handleDeleteFan = async (fanId) => {
    const confirmed = await confirm({
      title: 'Supprimer le fan',
      message: 'Êtes-vous sûr de vouloir supprimer ce compte fan ?',
      type: 'danger',
      confirmText: 'Supprimer',
      cancelText: 'Annuler'
    });
    if (!confirmed) return;
    try {
      await api.delete(`/admin/fans/${fanId}`);
      toast.success('Fan supprimé');
      fetchFans(fansPage, fansSearch);
      setFanDetailOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Execute pending transfers
  const handleExecuteTransfers = async () => {
    const message = t('executeTransfersDesc')
      .replace('{count}', pendingTransfers.ready_count)
      .replace('{total}', `${pendingTransfers.ready_total.toFixed(2)}€`);
    
    const confirmed = await confirm({
      title: t('executeTransfers'),
      message: message,
      type: 'warning',
      confirmText: t('confirm'),
      cancelText: t('cancel')
    });
    if (!confirmed) return;
    
    setExecutingTransfers(true);
    try {
      const res = await api.post('/stripe/execute-transfers');
      toast.success(`${res.data.success.length} transfert(s) exécuté(s) - Total: ${res.data.total_transferred.toFixed(2)}€`);
      if (res.data.failed.length > 0) {
        toast.error(`${res.data.failed.length} transfert(s) échoué(s)`);
      }
      fetchPendingTransfers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors des transferts');
    } finally {
      setExecutingTransfers(false);
    }
  };

  const handleUpdateConfig = async () => {
    setSavingConfig(true);
    try {
      await api.put('/admin/platform-config', platformConfig);
      toast.success('Configuration mise à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSavingConfig(false);
    }
  };

  // Artist delete function
  const handleDeleteArtist = async () => {
    if (!selectedArtistForAction) return;
    setActionLoading(true);
    try {
      await api.delete(`/admin/artists/${selectedArtistForAction.id}`);
      toast.success(`Artiste "${selectedArtistForAction.nomDeScene}" supprimé avec succès`);
      setDeleteConfirmOpen(false);
      setSelectedArtistForAction(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setActionLoading(false);
    }
  };

  // Artist reset password function
  const handleResetPassword = async () => {
    if (!selectedArtistForAction) return;
    setActionLoading(true);
    try {
      const res = await api.post(`/admin/artists/${selectedArtistForAction.id}/reset-password`);
      setGeneratedPassword({
        email: res.data.email,
        password: res.data.newPassword
      });
      toast.success('Mot de passe réinitialisé');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setActionLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié !');
  };

  const handleCreateApporteur = async (e) => {
    e.preventDefault();
    
    // Validation: au moins Wave OU Orange Money requis
    if (!apporteurForm.phoneWave && !apporteurForm.phoneOrangeMoney) {
      toast.error('Veuillez renseigner au moins un numéro Wave ou Orange Money');
      return;
    }
    
    try {
      const res = await api.post('/admin/apporteurs', apporteurForm);
      toast.success(`Apporteur créé ! Code: ${res.data.code}`);
      if (res.data.defaultPassword) {
        toast.info(`Mot de passe par défaut: ${res.data.defaultPassword}`);
      }
      setApporteurDialogOpen(false);
      setApporteurForm({ firstName: '', lastName: '', phoneWave: '', phoneOrangeMoney: '', iban: '', bic: '', email: '', stripe_connect_enabled: false });
      setEditingApporteur(null);
      fetchApporteurs();
      // Afficher les détails
      setSelectedApporteur(res.data);
      setApporteurDetailDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdateApporteur = async (e) => {
    e.preventDefault();
    
    // Validation: au moins Wave OU Orange Money requis
    if (!apporteurForm.phoneWave && !apporteurForm.phoneOrangeMoney) {
      toast.error('Veuillez renseigner au moins un numéro Wave ou Orange Money');
      return;
    }
    
    try {
      await api.put(`/admin/apporteurs/${editingApporteur.id}`, {
        phoneWave: apporteurForm.phoneWave,
        phoneOrangeMoney: apporteurForm.phoneOrangeMoney,
        iban: apporteurForm.iban,
        bic: apporteurForm.bic
      });
      toast.success('Apporteur mis à jour !');
      setApporteurDialogOpen(false);
      setApporteurForm({ firstName: '', lastName: '', phoneWave: '', phoneOrangeMoney: '', iban: '', bic: '', email: '', stripe_connect_enabled: false });
      setEditingApporteur(null);
      fetchApporteurs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleEditApporteur = (apporteur) => {
    setEditingApporteur(apporteur);
    setApporteurForm({
      firstName: apporteur.firstName || '',
      lastName: apporteur.lastName || '',
      phoneWave: apporteur.phoneWave || '',
      phoneOrangeMoney: apporteur.phoneOrangeMoney || '',
      iban: apporteur.iban || '',
      bic: apporteur.bic || '',
      email: apporteur.email || '',
      stripe_connect_enabled: apporteur.stripe_connect_enabled || false
    });
    setApporteurDialogOpen(true);
  };

  const handleToggleApporteurStatus = async (apporteurId, newStatus) => {
    try {
      await api.patch(`/admin/apporteurs/${apporteurId}`, { status: newStatus });
      toast.success(newStatus === 'active' ? 'Apporteur activé' : 'Apporteur suspendu');
      fetchApporteurs();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteApporteur = async (apporteurId) => {
    const confirmed = await confirm({
      title: t('deleteConfirmTitle'),
      message: t('deleteApporteurDesc'),
      type: 'danger',
      confirmText: t('delete'),
      cancelText: t('cancel')
    });
    if (!confirmed) return;
    try {
      const res = await api.delete(`/admin/apporteurs/${apporteurId}`);
      toast.success(res.data.message);
      fetchApporteurs();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleViewApporteurDetail = async (apporteurId) => {
    try {
      const res = await api.get(`/admin/apporteurs/${apporteurId}`);
      setSelectedApporteur(res.data);
      setApporteurDetailDialogOpen(true);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const formatPrice = (price, currency = 'XOF') => {
    const currencyCode = currency === 'FCFA' ? 'XOF' : currency;
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currencyCode, minimumFractionDigits: 0 }).format(price);
  };

  const filteredArtists = artists.filter(artist => 
    artist.nomDeScene?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    artist.slug?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-2 sm:px-3 py-1 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <SenArtisteLogo className="w-12 h-12" />
            <span className="font-syne font-bold text-xl">Sen Artiste</span>
            <span className="px-2 py-0.5 text-xs rounded-full bg-primary/20 text-primary">Admin</span>
          </div>
          
          <div className="flex items-center gap-2">
            <LanguageSelector />
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-sm font-medium">{user?.firstName?.[0]}{user?.lastName?.[0]}</span>
              </div>
              <span className="hidden sm:block text-sm">{user?.firstName}</span>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout}>
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 flex gap-1 flex-wrap">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'dashboard' ? 'border-primary text-primary' : 'border-transparent hover:text-primary'}`}
            data-testid="tab-dashboard"
          >
            <LayoutDashboard className="w-4 h-4 inline mr-2" />
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab('reversements')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'reversements' ? 'border-primary text-primary' : 'border-transparent hover:text-primary'}`}
            data-testid="tab-reversements"
          >
            <Banknote className="w-4 h-4 inline mr-2" />
            Reversements
            {reversementsSummary.events_due_for_withdrawal > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">{reversementsSummary.events_due_for_withdrawal}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'notifications' ? 'border-primary text-primary' : 'border-transparent hover:text-primary'}`}
            data-testid="tab-notifications"
          >
            <Bell className="w-4 h-4 inline mr-2" />
            Notifications
            {notificationsCount.unprocessed > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-orange-500 text-white rounded-full">{notificationsCount.unprocessed}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('artists')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'artists' ? 'border-primary text-primary' : 'border-transparent hover:text-primary'}`}
            data-testid="tab-artists"
          >
            <Music className="w-4 h-4 inline mr-2" />
            Artistes
          </button>
          <button
            onClick={() => setActiveTab('banners')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'banners' ? 'border-primary text-primary' : 'border-transparent hover:text-primary'}`}
            data-testid="tab-banners"
          >
            <Megaphone className="w-4 h-4 inline mr-2" />
            Bannières
          </button>
          <button
            onClick={() => setActiveTab('apporteurs')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'apporteurs' ? 'border-primary text-primary' : 'border-transparent hover:text-primary'}`}
            data-testid="tab-apporteurs"
          >
            <UserPlus className="w-4 h-4 inline mr-2" />
            Apporteurs
          </button>
          <button
            onClick={() => { setActiveTab('fans'); fetchFans(1, ''); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'fans' ? 'border-primary text-primary' : 'border-transparent hover:text-primary'}`}
            data-testid="tab-fans"
          >
            <Users className="w-4 h-4 inline mr-2" />
            Fans
          </button>
          <button
            onClick={() => { setActiveTab('incidents'); fetchIncidents(1); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'incidents' ? 'border-primary text-primary' : 'border-transparent hover:text-primary'}`}
            data-testid="tab-incidents"
          >
            <MessageCircleQuestion className="w-4 h-4 inline mr-2" />
            Incidents
            {incidentsStats.new > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">{incidentsStats.new}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('config')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'config' ? 'border-primary text-primary' : 'border-transparent hover:text-primary'}`}
            data-testid="tab-config"
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Configuration
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
            data-testid="admin-dashboard"
          >
            <h1 className="font-syne text-3xl font-bold">Dashboard Global</h1>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="glass-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Artistes actifs</p>
                      <p className="text-3xl font-bold">{stats.totalArtists || 0}</p>
                    </div>
                    <Music className="w-10 h-10 text-primary opacity-50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="glass-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Fans</p>
                      <p className="text-3xl font-bold">{stats.totalFans || 0}</p>
                    </div>
                    <Users className="w-10 h-10 text-primary opacity-50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="glass-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Billets vendus</p>
                      <p className="text-3xl font-bold">{stats.totalTickets || 0}</p>
                    </div>
                    <Ticket className="w-10 h-10 text-primary opacity-50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="glass-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Commission plateforme</p>
                      <p className="text-xl font-bold">{formatPrice(stats.platformRevenueFCFA || 0, 'FCFA')}</p>
                      <p className="text-sm font-medium text-blue-500">+ {formatPrice(stats.platformRevenueEUR || 0, 'EUR')}</p>
                    </div>
                    <DollarSign className="w-10 h-10 text-primary opacity-50" />
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Revenus séparés par devise */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="glass-card border-l-4 border-l-green-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-600 text-xs font-bold">F</span>
                    Revenus FCFA
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-green-500">{formatPrice(stats.totalRevenueFCFA || 0, 'FCFA')}</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Commission plateforme (5%): {formatPrice(stats.platformRevenueFCFA || 0, 'FCFA')}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Paiements: Orange Money / Wave
                  </p>
                </CardContent>
              </Card>
              
              <Card className="glass-card border-l-4 border-l-blue-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-600 text-xs font-bold">€</span>
                    Revenus EUR
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-blue-500">{formatPrice(stats.totalRevenueEUR || 0, 'EUR')}</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Commission plateforme (5%): {formatPrice(stats.platformRevenueEUR || 0, 'EUR')}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Paiements: Carte Bancaire (SEPA)
                  </p>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        )}

        {/* Reversements Tab */}
        {activeTab === 'reversements' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
            data-testid="admin-reversements"
          >
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <h1 className="font-syne text-3xl font-bold">Gestion des Reversements</h1>
              <Button onClick={fetchReversements} variant="outline" size="sm">
                <Loader2 className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
            </div>

            {/* Totaux */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="glass-card border-l-4 border-l-green-500">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">Total généré EUR</p>
                  <p className="text-2xl font-bold text-green-600">{reversementsTotals.eur?.total_generated?.toFixed(2) || 0}€</p>
                </CardContent>
              </Card>
              <Card className="glass-card border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">À reverser EUR</p>
                  <p className="text-2xl font-bold text-blue-600">{reversementsTotals.eur?.total_pending?.toFixed(2) || 0}€</p>
                </CardContent>
              </Card>
              <Card className="glass-card border-l-4 border-l-green-500">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">Total généré FCFA</p>
                  <p className="text-2xl font-bold text-green-600">{(reversementsTotals.fcfa?.total_generated || 0).toLocaleString()} F</p>
                </CardContent>
              </Card>
              <Card className="glass-card border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">À reverser FCFA</p>
                  <p className="text-2xl font-bold text-blue-600">{(reversementsTotals.fcfa?.total_pending || 0).toLocaleString()} F</p>
                </CardContent>
              </Card>
            </div>

            {/* Mes reversements plateforme (part admin) */}
            <Card className="glass-card" data-testid="platform-payouts-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Banknote className="w-5 h-5 text-primary" />
                  Mes reversements (part plateforme → {platformConfig.platform_payment_number || '+221778312002'})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                  <div className="p-3 bg-green-500/10 rounded-lg">
                    <p className="text-xl font-bold text-green-600" data-testid="platform-payouts-net-received">{(platformPayoutsTotals.net_received || 0).toLocaleString()} F</p>
                    <p className="text-xs text-muted-foreground">Reçu sur mon numéro</p>
                  </div>
                  <div className="p-3 bg-red-500/10 rounded-lg">
                    <p className="text-xl font-bold text-red-600">{(platformPayoutsTotals.net_failed || 0).toLocaleString()} F</p>
                    <p className="text-xs text-muted-foreground">Échoués / non envoyés</p>
                  </div>
                  <div className="p-3 bg-blue-500/10 rounded-lg">
                    <p className="text-xl font-bold text-blue-600">{(platformPayoutsTotals.reserve_kept || 0).toLocaleString()} F</p>
                    <p className="text-xs text-muted-foreground">Réserve plateforme (1%)</p>
                  </div>
                  <div className="p-3 bg-orange-500/10 rounded-lg">
                    <p className="text-xl font-bold text-orange-600">{(platformPayoutsTotals.fees_paid || 0).toLocaleString()} F</p>
                    <p className="text-xs text-muted-foreground">Frais Wave/OM supportés</p>
                  </div>
                </div>
                {platformPayouts.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4" data-testid="platform-payouts-empty">Aucun reversement plateforme pour le moment</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="platform-payouts-table">
                      <thead>
                        <tr className="text-left text-muted-foreground border-b border-border">
                          <th className="py-2 pr-3">Date</th>
                          <th className="py-2 pr-3">Artiste / Événement</th>
                          <th className="py-2 pr-3">Méthode</th>
                          <th className="py-2 pr-3 text-right">Artiste</th>
                          <th className="py-2 pr-3 text-right">Ma part</th>
                          <th className="py-2 pr-3 text-right">Frais</th>
                          <th className="py-2 pr-3 text-right">Réserve</th>
                          <th className="py-2 pr-3">Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {platformPayouts.slice(0, 50).map((pp) => (
                          <tr key={pp.id} className="border-b border-border/50" data-testid={`platform-payout-row-${pp.id}`}>
                            <td className="py-2 pr-3 whitespace-nowrap">{new Date(pp.createdAt).toLocaleDateString('fr-FR')}</td>
                            <td className="py-2 pr-3">
                              <p className="font-medium">{pp.artistName}</p>
                              <p className="text-xs text-muted-foreground">{pp.eventTitle}</p>
                            </td>
                            <td className="py-2 pr-3 uppercase">{pp.payoutMethod}</td>
                            <td className="py-2 pr-3 text-right">{(pp.artistAmount || 0).toLocaleString()} F</td>
                            <td className="py-2 pr-3 text-right font-bold text-green-600">{(pp.platformNetAmount || 0).toLocaleString()} F</td>
                            <td className="py-2 pr-3 text-right text-orange-600">{((pp.artistTransferFee || 0) + (pp.platformTransferFee || 0)).toLocaleString()} F</td>
                            <td className="py-2 pr-3 text-right text-blue-600">{(pp.platformReserve || 0).toLocaleString()} F</td>
                            <td className="py-2 pr-3">
                              {pp.success ? (
                                <Badge className="bg-green-500/20 text-green-600 border-0">Envoyé</Badge>
                              ) : (
                                <Badge className="bg-red-500/20 text-red-600 border-0" title={pp.error || ''}>Échec</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Alerte reversements en retard */}
            {reversementsSummary.events_due_for_withdrawal > 0 && (
              <Card className="bg-red-500/10 border-red-500">
                <CardContent className="p-4 flex items-center gap-4">
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                  <div>
                    <p className="font-bold text-red-600">{reversementsSummary.events_due_for_withdrawal} reversement(s) en retard !</p>
                    <p className="text-sm text-muted-foreground">Ces reversements ont dépassé le délai de 10 jours</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Reversements en attente (échoués - traitement manuel requis) */}
            {pendingPayouts.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-syne text-xl font-bold text-red-600 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Reversements à traiter manuellement ({pendingPayouts.length})
                  </h2>
                  <Button onClick={fetchPendingPayouts} variant="outline" size="sm">
                    Actualiser
                  </Button>
                </div>
                
                <div className="grid gap-3">
                  {pendingPayouts.map((payout) => (
                    <Card key={payout.id} className="border-red-500/50 bg-red-500/5">
                      <CardContent className="p-4">
                        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                                payout.payoutMethod === 'wave' 
                                  ? 'bg-blue-500/20 text-blue-600' 
                                  : 'bg-orange-500/20 text-orange-600'
                              }`}>
                                {payout.payoutMethod === 'wave' ? 'WAVE' : 'ORANGE MONEY'}
                              </span>
                              <span className="font-bold text-lg">{payout.amount.toLocaleString()} FCFA</span>
                            </div>
                            <p className="text-sm"><strong>Artiste:</strong> {payout.artistName}</p>
                            <p className="text-sm"><strong>Événement:</strong> {payout.eventTitle}</p>
                            <p className="text-sm"><strong>Mobile:</strong> {payout.mobile}</p>
                            <p className="text-xs text-red-500 mt-1"><strong>Erreur:</strong> {payout.errorMessage}</p>
                            <p className="text-xs text-muted-foreground">
                              Demandé le: {new Date(payout.createdAt).toLocaleString('fr-FR')}
                            </p>
                          </div>
                          
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              disabled={processingPayout === payout.id}
                              onClick={() => {
                                const ref = window.prompt('Référence du transfert effectué:');
                                if (ref !== null) {
                                  handleMarkPayoutDone(payout.id, 'Traité manuellement', ref);
                                }
                              }}
                            >
                              {processingPayout === payout.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Check className="w-4 h-4 mr-1" />
                              )}
                              Marquer fait
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-600"
                              disabled={processingPayout === payout.id}
                              onClick={() => handleCancelPendingPayout(payout.id)}
                            >
                              Annuler
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Liste des événements avec reversements */}
            <div className="space-y-4">
              {reversements.length === 0 ? (
                <Card className="glass-card">
                  <CardContent className="p-8 text-center">
                    <Banknote className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Aucun reversement en attente</p>
                  </CardContent>
                </Card>
              ) : (
                reversements.map((rev) => (
                  <Card key={rev.eventId} className={`glass-card ${rev.isDue ? 'border-red-500 border-2' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col lg:flex-row justify-between gap-4">
                        {/* Info événement et artiste */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {rev.isDue && <AlertTriangle className="w-5 h-5 text-red-500" />}
                            <span className={`px-2 py-0.5 text-xs rounded ${rev.currency === 'EUR' ? 'bg-blue-500/20 text-blue-600' : 'bg-orange-500/20 text-orange-600'}`}>
                              {rev.currency}
                            </span>
                            <h3 className="font-bold text-lg">{rev.eventTitle}</h3>
                          </div>
                          <p className="text-sm text-muted-foreground mb-1">
                            <Music className="w-4 h-4 inline mr-1" />
                            Artiste: <span className="font-medium">{rev.artistName}</span>
                          </p>
                          {rev.artistPhone && (
                            <p className="text-sm text-muted-foreground">
                              <Phone className="w-4 h-4 inline mr-1" />
                              {rev.artistWave && <span className="mr-2">Wave: {rev.artistWave}</span>}
                              {rev.artistOrangeMoney && <span>OM: {rev.artistOrangeMoney}</span>}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground mt-2">
                            <Calendar className="w-3 h-3 inline mr-1" />
                            Date événement: {rev.eventDate ? new Date(rev.eventDate).toLocaleDateString('fr-FR') : 'N/A'}
                          </p>
                        </div>

                        {/* Montants */}
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div className="p-2 bg-green-500/10 rounded">
                            <p className="text-xs text-muted-foreground">Généré</p>
                            <p className="font-bold text-green-600">
                              {rev.currency === 'EUR' ? `${rev.totalGenerated.toFixed(2)}€` : `${rev.totalGenerated.toLocaleString()} F`}
                            </p>
                          </div>
                          <div className="p-2 bg-blue-500/10 rounded">
                            <p className="text-xs text-muted-foreground">À reverser</p>
                            <p className="font-bold text-blue-600">
                              {rev.currency === 'EUR' ? `${rev.totalPending.toFixed(2)}€` : `${rev.totalPending.toLocaleString()} F`}
                            </p>
                          </div>
                          <div className="p-2 bg-gray-500/10 rounded">
                            <p className="text-xs text-muted-foreground">Déjà reversé</p>
                            <p className="font-bold text-gray-600">
                              {rev.currency === 'EUR' ? `${rev.totalWithdrawn.toFixed(2)}€` : `${rev.totalWithdrawn.toLocaleString()} F`}
                            </p>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col justify-center gap-2">
                          {rev.totalPending > 0 ? (
                            <>
                              <p className="text-xs text-center text-muted-foreground">
                                {rev.withdrawalDueDate && (
                                  <>Échéance: {new Date(rev.withdrawalDueDate).toLocaleDateString('fr-FR')}</>
                                )}
                              </p>
                              <Button
                                size="sm"
                                onClick={() => {
                                  const method = rev.currency === 'EUR' ? 'bank_transfer' : 'mobile_money';
                                  const reference = window.prompt('Référence du virement (optionnel):');
                                  if (reference !== null) {
                                    handleMarkWithdrawn(rev.eventId, rev.currency, method, reference);
                                  }
                                }}
                                disabled={markingWithdrawn === rev.eventId}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                {markingWithdrawn === rev.eventId ? (
                                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                ) : (
                                  <Check className="w-4 h-4 mr-1" />
                                )}
                                Marquer reversé
                              </Button>
                            </>
                          ) : (
                            <div className="flex items-center gap-2 text-green-600">
                              <CheckCircle className="w-5 h-5" />
                              <span className="text-sm font-medium">Tout reversé</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </motion.div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
            data-testid="admin-notifications"
          >
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <h1 className="font-syne text-3xl font-bold">Notifications</h1>
              <div className="flex gap-2">
                <span className="px-3 py-1 bg-orange-500/20 text-orange-600 rounded-full text-sm">
                  {notificationsCount.unprocessed} non traitée(s)
                </span>
                <Button onClick={fetchAdminNotifications} variant="outline" size="sm">
                  <Loader2 className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Actualiser
                </Button>
              </div>
            </div>

            {/* Liste des notifications */}
            <div className="space-y-3">
              {adminNotifications.length === 0 ? (
                <Card className="glass-card">
                  <CardContent className="p-8 text-center">
                    <Bell className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Aucune notification</p>
                  </CardContent>
                </Card>
              ) : (
                adminNotifications.map((notif) => (
                  <Card 
                    key={notif.id} 
                    className={`glass-card transition-all ${!notif.isRead ? 'border-l-4 border-l-blue-500 bg-blue-500/5' : ''} ${notif.isProcessed ? 'opacity-60' : ''}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {notif.type === 'withdrawal_reminder' && <Clock className="w-5 h-5 text-orange-500" />}
                            {notif.type === 'withdrawal_completed' && <CheckCircle className="w-5 h-5 text-green-500" />}
                            {notif.type === 'new_sale' && <Ticket className="w-5 h-5 text-blue-500" />}
                            <h3 className="font-medium">{notif.title}</h3>
                            {!notif.isRead && <span className="px-2 py-0.5 text-xs bg-blue-500 text-white rounded">Nouveau</span>}
                            {notif.isProcessed && <span className="px-2 py-0.5 text-xs bg-green-500 text-white rounded">Traité</span>}
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{notif.message}</p>
                          <div className="flex gap-4 text-xs text-muted-foreground">
                            <span>
                              <Calendar className="w-3 h-3 inline mr-1" />
                              {new Date(notif.createdAt).toLocaleString('fr-FR')}
                            </span>
                            {notif.dueDate && (
                              <span className={new Date(notif.dueDate) < new Date() ? 'text-red-500 font-medium' : ''}>
                                <Clock className="w-3 h-3 inline mr-1" />
                                Échéance: {new Date(notif.dueDate).toLocaleDateString('fr-FR')}
                              </span>
                            )}
                            {notif.amount && (
                              <span className="font-medium">
                                <DollarSign className="w-3 h-3 inline mr-1" />
                                {notif.currency === 'EUR' ? `${notif.amount.toFixed(2)}€` : `${notif.amount.toLocaleString()} FCFA`}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex flex-col gap-1">
                          {!notif.isProcessed && (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => handleMarkNotificationProcessed(notif.id)}
                              className="text-green-600 border-green-600 hover:bg-green-600 hover:text-white"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Traité
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleDeleteNotification(notif.id)}
                            className="text-red-500 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </motion.div>
        )}

        {/* Artists Tab */}
        {activeTab === 'artists' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
            data-testid="admin-artists"
          >
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <h1 className="font-syne text-3xl font-bold">Gestion des Artistes</h1>
              
              <div className="flex gap-4 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input 
                    placeholder="Rechercher..." 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-artists"
                  />
                </div>
                
                <Dialog open={artistDialogOpen} onOpenChange={setArtistDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" data-testid="add-artist-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Nouvel artiste</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateArtist} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Slug (URL)</Label>
                        <Input 
                          value={artistForm.slug} 
                          onChange={(e) => setArtistForm(p => ({ ...p, slug: e.target.value.toLowerCase().replace(/\s+/g, '') }))}
                          placeholder="nomartiste"
                          required
                          data-testid="artist-slug"
                        />
                        <p className="text-xs text-muted-foreground">URL: senartiste.com/{artistForm.slug || 'slug'}</p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Prénom</Label>
                          <Input 
                            value={artistForm.prenom} 
                            onChange={(e) => setArtistForm(p => ({ ...p, prenom: e.target.value }))}
                            required
                            data-testid="artist-prenom"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Nom</Label>
                          <Input 
                            value={artistForm.nom} 
                            onChange={(e) => setArtistForm(p => ({ ...p, nom: e.target.value }))}
                            required
                            data-testid="artist-nom"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Nom de scène</Label>
                        <Input 
                          value={artistForm.nomDeScene} 
                          onChange={(e) => setArtistForm(p => ({ ...p, nomDeScene: e.target.value }))}
                          required
                          data-testid="artist-nomDeScene"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Email contact</Label>
                          <Input 
                            type="email"
                            value={artistForm.emailContact} 
                            onChange={(e) => setArtistForm(p => ({ ...p, emailContact: e.target.value }))}
                            data-testid="artist-email"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Téléphone</Label>
                          <Input 
                            value={artistForm.telephoneContact} 
                            onChange={(e) => setArtistForm(p => ({ ...p, telephoneContact: e.target.value }))}
                            data-testid="artist-phone"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Couleur principale</Label>
                          <div className="flex gap-2">
                            <Input 
                              type="color"
                              value={artistForm.primaryColor} 
                              onChange={(e) => setArtistForm(p => ({ ...p, primaryColor: e.target.value }))}
                              className="w-16 h-10 p-1"
                            />
                            <Input 
                              value={artistForm.primaryColor} 
                              onChange={(e) => setArtistForm(p => ({ ...p, primaryColor: e.target.value }))}
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Couleur secondaire</Label>
                          <div className="flex gap-2">
                            <Input 
                              type="color"
                              value={artistForm.secondaryColor} 
                              onChange={(e) => setArtistForm(p => ({ ...p, secondaryColor: e.target.value }))}
                              className="w-16 h-10 p-1"
                            />
                            <Input 
                              value={artistForm.secondaryColor} 
                              onChange={(e) => setArtistForm(p => ({ ...p, secondaryColor: e.target.value }))}
                            />
                          </div>
                        </div>
                      </div>
                      
                      <DialogFooter>
                        <Button type="submit" className="btn-primary" data-testid="create-artist-submit">
                          Créer l'artiste
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
            
            <div className="grid gap-4">
              {filteredArtists.map(artist => (
                <Card key={artist.id} className="glass-card" data-testid={`artist-card-${artist.id}`}>
                  <CardContent className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: artist.primaryColor || '#D4AF37' }}
                      >
                        {artist.nomDeScene?.[0] || 'A'}
                      </div>
                      <div>
                        <h3 className="font-bold">{artist.nomDeScene}</h3>
                        <p className="text-sm text-muted-foreground">{artist.prenom} {artist.nom}</p>
                        <p className="text-xs text-primary">/{artist.slug}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-3 py-1 rounded-full text-sm ${artist.isActive ? 'bg-green-500/20 text-green-600' : 'bg-red-500/20 text-red-600'}`}>
                        {artist.isActive ? 'Actif' : 'Inactif'}
                      </span>
                      {artist.isPremium && (
                        <span className="px-3 py-1 rounded-full text-sm bg-yellow-500/20 text-yellow-600 flex items-center gap-1">
                          <Star className="w-3 h-3" />
                          Premium
                        </span>
                      )}
                      
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleToggleArtistPremium(artist.id, artist.isPremium)}
                        title={artist.isPremium ? "Retirer Premium" : "Passer en Premium"}
                        className={artist.isPremium ? "border-yellow-500 text-yellow-600 hover:bg-yellow-500/10" : ""}
                      >
                        <Star className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => navigate(`/${artist.slug}`)} title="Voir la page">
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleToggleArtistStatus(artist.id)} title="Activer/Désactiver">
                        <Power className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => {
                          setSelectedArtistForAction(artist);
                          setGeneratedPassword(null);
                          setResetPasswordOpen(true);
                        }}
                        title="Réinitialiser le mot de passe"
                      >
                        <Key className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-red-500 hover:bg-red-500/10"
                        onClick={() => {
                          setSelectedArtistForAction(artist);
                          setDeleteConfirmOpen(true);
                        }}
                        title="Supprimer l'artiste"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        )}

        {/* Banners Tab */}
        {activeTab === 'banners' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
            data-testid="admin-banners"
          >
            <div className="flex justify-between items-center">
              <h1 className="font-syne text-3xl font-bold">Bannières Globales</h1>
              
              <Dialog open={bannerDialogOpen} onOpenChange={(open) => {
                setBannerDialogOpen(open);
                if (!open) {
                  setEditingBanner(null);
                  setBannerForm({ title: '', description: '', imageUrl: '', contactPhone: '', contactEmail: '', shopUrl: '', isActive: true, targetTenants: [] });
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-global-banner-btn" onClick={() => {
                    setEditingBanner(null);
                    setBannerForm({ title: '', description: '', imageUrl: '', contactPhone: '', contactEmail: '', shopUrl: '', isActive: true, targetTenants: [] });
                  }}>
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingBanner ? 'Modifier la bannière' : 'Nouvelle bannière globale'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateBanner} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Titre</Label>
                      <Input 
                        value={bannerForm.title} 
                        onChange={(e) => setBannerForm(p => ({ ...p, title: e.target.value }))}
                        required
                        data-testid="global-banner-title"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Input 
                        value={bannerForm.description} 
                        onChange={(e) => setBannerForm(p => ({ ...p, description: e.target.value }))}
                        data-testid="global-banner-description"
                      />
                    </div>
                    
                    {/* Upload d'image */}
                    <div className="space-y-2">
                      <Label>Image de la bannière *</Label>
                      <input
                        type="file"
                        ref={bannerFileInputRef}
                        onChange={handleBannerImageUpload}
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                        className="hidden"
                      />
                      
                      {bannerForm.imageUrl ? (
                        <div className="relative">
                          <div className="w-full h-32 rounded-lg border border-border bg-muted/50 flex items-center justify-center overflow-hidden">
                            <img 
                              src={bannerForm.imageUrl} 
                              alt="Aperçu bannière" 
                              className="max-w-full max-h-full object-contain"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="absolute bottom-2 right-2"
                            onClick={() => bannerFileInputRef.current?.click()}
                            disabled={uploadingBanner}
                          >
                            {uploadingBanner ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-1" />
                                Changer
                              </>
                            )}
                          </Button>
                        </div>
                      ) : (
                        <div 
                          className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary transition-colors"
                          onClick={() => bannerFileInputRef.current?.click()}
                        >
                          {uploadingBanner ? (
                            <div className="flex flex-col items-center gap-2">
                              <Loader2 className="w-8 h-8 animate-spin text-primary" />
                              <p className="text-sm text-muted-foreground">Upload en cours...</p>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center gap-2">
                              <ImageIcon className="w-8 h-8 text-muted-foreground" />
                              <p className="text-sm text-muted-foreground">
                                Cliquez pour uploader une image
                              </p>
                              <p className="text-xs text-muted-foreground">
                                JPG, PNG, GIF, WEBP (max 10MB)
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Téléphone</Label>
                        <Input 
                          value={bannerForm.contactPhone} 
                          onChange={(e) => setBannerForm(p => ({ ...p, contactPhone: e.target.value }))}
                          placeholder="+221 77 000 00 00"
                          data-testid="global-banner-phone"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input 
                          type="email"
                          value={bannerForm.contactEmail} 
                          onChange={(e) => setBannerForm(p => ({ ...p, contactEmail: e.target.value }))}
                          placeholder="contact@exemple.com"
                          data-testid="global-banner-email"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Lien boutique / site web</Label>
                      <Input 
                        value={bannerForm.shopUrl} 
                        onChange={(e) => setBannerForm(p => ({ ...p, shopUrl: e.target.value }))}
                        placeholder="https://boutique.exemple.com"
                        data-testid="global-banner-shopUrl"
                      />
                    </div>
                    <DialogFooter>
                      <Button type="submit" className="btn-primary" disabled={!bannerForm.imageUrl || uploadingBanner} data-testid="create-global-banner-submit">
                        {editingBanner ? 'Enregistrer' : 'Créer'}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            
            <div className="grid gap-4 md:grid-cols-2">
              {globalBanners.map(banner => (
                <Card key={banner.id} className="glass-card overflow-hidden" data-testid={`global-banner-${banner.id}`}>
                  {banner.imageUrl && (
                    <div className="h-40">
                      <img src={banner.imageUrl} alt={banner.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold">{banner.title}</h3>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${banner.isActive ? 'bg-green-500/20 text-green-600' : 'bg-gray-500/20 text-gray-600'}`}>
                        {banner.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {banner.description && <p className="text-sm text-muted-foreground mb-2">{banner.description}</p>}
                    <div className="space-y-1 text-xs text-muted-foreground">
                      {banner.contactPhone && (
                        <p>📞 {banner.contactPhone}</p>
                      )}
                      {banner.contactEmail && (
                        <p>✉️ {banner.contactEmail}</p>
                      )}
                      {banner.shopUrl && (
                        <p>🔗 <a href={banner.shopUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{banner.shopUrl}</a></p>
                      )}
                    </div>
                    <p className="text-xs text-primary mt-2">
                      {banner.targetTenants?.length > 0 ? `${banner.targetTenants.length} tenant(s) ciblé(s)` : 'Tous les tenants'}
                    </p>
                    
                    {/* Boutons de modification et suppression */}
                    <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => handleEditBanner(banner)}
                        data-testid={`edit-banner-${banner.id}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Modifier
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleToggleBannerStatus(banner.id, !banner.isActive)}
                        data-testid={`toggle-banner-${banner.id}`}
                      >
                        {banner.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => handleDeleteBanner(banner.id)}
                        data-testid={`delete-banner-${banner.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        )}

        {/* Apporteurs Tab */}
        {activeTab === 'apporteurs' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
            data-testid="admin-apporteurs"
          >
            <div className="flex justify-between items-center">
              <div>
                <h1 className="font-syne text-3xl font-bold">Apporteurs (Parrains)</h1>
                <p className="text-muted-foreground mt-1">
                  Gérez les apporteurs qui parrainent les artistes sur la plateforme
                </p>
              </div>
              
              <Dialog open={apporteurDialogOpen} onOpenChange={(open) => {
                if (!open) {
                  setEditingApporteur(null);
                  setApporteurForm({ firstName: '', lastName: '', phoneWave: '', phoneOrangeMoney: '', iban: '', bic: '', email: '', stripe_connect_enabled: false });
                }
                setApporteurDialogOpen(open);
              }}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-apporteur-btn" onClick={() => {
                    setEditingApporteur(null);
                    setApporteurForm({ firstName: '', lastName: '', phoneWave: '', phoneOrangeMoney: '', iban: '', bic: '', email: '', stripe_connect_enabled: false });
                  }}>
                    <Plus className="w-4 h-4 mr-2" />
                    Créer un apporteur
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingApporteur ? 'Modifier l\'apporteur' : 'Créer un apporteur'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={editingApporteur ? handleUpdateApporteur : handleCreateApporteur} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Prénom *</Label>
                        <Input 
                          value={apporteurForm.firstName} 
                          onChange={(e) => setApporteurForm(p => ({ ...p, firstName: e.target.value }))}
                          required={!editingApporteur}
                          disabled={editingApporteur}
                          data-testid="apporteur-firstName"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Nom *</Label>
                        <Input 
                          value={apporteurForm.lastName} 
                          onChange={(e) => setApporteurForm(p => ({ ...p, lastName: e.target.value }))}
                          required={!editingApporteur}
                          disabled={editingApporteur}
                          data-testid="apporteur-lastName"
                        />
                      </div>
                    </div>
                    
                    {!editingApporteur && (
                      <div className="space-y-2">
                        <Label>Email (pour connexion au dashboard)</Label>
                        <Input 
                          type="email"
                          value={apporteurForm.email} 
                          onChange={(e) => setApporteurForm(p => ({ ...p, email: e.target.value }))}
                          placeholder="apporteur@exemple.com"
                          data-testid="apporteur-email"
                        />
                        <p className="text-xs text-muted-foreground">
                          Si fourni, un compte sera créé avec mot de passe par défaut
                        </p>
                      </div>
                    )}
                    
                    <div className="border-t border-border pt-4">
                      <p className="text-sm font-medium mb-3">
                        Informations de paiement FCFA <span className="text-red-500">*</span>
                      </p>
                      <p className="text-xs text-muted-foreground mb-3">
                        Au moins un numéro Wave ou Orange Money est obligatoire
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                            Téléphone Wave
                          </Label>
                          <Input 
                            value={apporteurForm.phoneWave} 
                            onChange={(e) => setApporteurForm(p => ({ ...p, phoneWave: e.target.value }))}
                            placeholder="+221 76 xxx xx xx"
                            data-testid="apporteur-phoneWave"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                            Téléphone Orange Money
                          </Label>
                          <Input 
                            value={apporteurForm.phoneOrangeMoney} 
                            onChange={(e) => setApporteurForm(p => ({ ...p, phoneOrangeMoney: e.target.value }))}
                            placeholder="+221 77 xxx xx xx"
                            data-testid="apporteur-phoneOrangeMoney"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div className="space-y-2">
                          <Label>IBAN (optionnel - pour virements EUR)</Label>
                          <Input 
                            value={apporteurForm.iban} 
                            onChange={(e) => setApporteurForm(p => ({ ...p, iban: e.target.value }))}
                            placeholder="FR76..."
                            data-testid="apporteur-iban"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>BIC (optionnel)</Label>
                          <Input 
                            value={apporteurForm.bic} 
                            onChange={(e) => setApporteurForm(p => ({ ...p, bic: e.target.value }))}
                            placeholder="BNPAFRPP"
                            data-testid="apporteur-bic"
                          />
                        </div>
                      </div>
                      
                      {/* Stripe Connect Option */}
                      {!editingApporteur && (
                        <div className="flex items-center justify-between p-3 border border-border rounded-lg mt-4">
                          <div>
                            <Label className="flex items-center gap-2">
                              <CreditCard className="w-4 h-4 text-blue-500" />
                              Activer Stripe Connect
                            </Label>
                            <p className="text-xs text-muted-foreground mt-1">
                              Permet à l'apporteur de recevoir des paiements EUR via Stripe
                            </p>
                          </div>
                          <input 
                            type="checkbox"
                            checked={apporteurForm.stripe_connect_enabled}
                            onChange={(e) => setApporteurForm(p => ({ ...p, stripe_connect_enabled: e.target.checked }))}
                            className="h-4 w-4"
                            data-testid="apporteur-stripe-enabled"
                          />
                        </div>
                      )}
                    </div>
                    
                    <DialogFooter>
                      <Button type="submit" className="btn-primary" data-testid="create-apporteur-submit">
                        {editingApporteur ? 'Enregistrer les modifications' : 'Créer l\'apporteur'}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Info Card */}
            <Card className="glass-card bg-gradient-to-r from-primary/10 to-transparent">
              <CardContent className="p-4">
                <p className="text-sm">
                  <strong>Commission apporteur :</strong> 1% du prix de chaque billet vendu par les artistes parrainés.
                  La commission est déduite de la part plateforme (pas de l'artiste).
                </p>
              </CardContent>
            </Card>

            {/* Apporteurs List */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {apporteurs.length === 0 ? (
                <Card className="glass-card col-span-full">
                  <CardContent className="p-8 text-center">
                    <UserPlus className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Aucun apporteur créé</p>
                  </CardContent>
                </Card>
              ) : (
                apporteurs.map(apporteur => (
                  <Card key={apporteur.id} className="glass-card" data-testid={`apporteur-card-${apporteur.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                            <span className="font-bold text-primary">
                              {apporteur.firstName?.[0]}{apporteur.lastName?.[0]}
                            </span>
                          </div>
                          <div>
                            <h3 className="font-bold">{apporteur.firstName} {apporteur.lastName}</h3>
                            <p className="text-xs text-muted-foreground">{apporteur.email || 'Pas d\'email'}</p>
                          </div>
                        </div>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${apporteur.status === 'active' ? 'bg-green-500/20 text-green-600' : 'bg-red-500/20 text-red-600'}`}>
                          {apporteur.status === 'active' ? 'Actif' : 'Suspendu'}
                        </span>
                      </div>
                      
                      <div className="p-3 bg-muted/50 rounded-lg mb-3">
                        <p className="text-xs text-muted-foreground mb-1">Code parrainage</p>
                        <div className="flex items-center justify-between">
                          <span className="font-mono font-bold text-lg text-primary">{apporteur.code}</span>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(apporteur.code);
                              toast.success('Code copié !');
                            }}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm mb-3">
                        <span className="text-muted-foreground">Artistes parrainés</span>
                        <span className="font-bold">{apporteur.artistsCount || 0}</span>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => handleViewApporteurDetail(apporteur.id)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Détails
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleEditApporteur(apporteur)}
                          data-testid={`edit-apporteur-${apporteur.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleToggleApporteurStatus(apporteur.id, apporteur.status === 'active' ? 'suspended' : 'active')}
                        >
                          {apporteur.status === 'active' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => handleDeleteApporteur(apporteur.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </motion.div>
        )}

        {/* Fans Tab */}
        {activeTab === 'fans' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
            data-testid="admin-fans"
          >
            <div className="flex items-center justify-between">
              <div>
                <h1 className="font-syne text-3xl font-bold">Gestion des Fans</h1>
                <p className="text-muted-foreground mt-1">Voir, modifier et supprimer les comptes fans</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher par email, nom..."
                    value={fansSearch}
                    onChange={(e) => setFansSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchFans(1, fansSearch)}
                    className="pl-10 w-64"
                  />
                </div>
                <Button variant="outline" onClick={() => fetchFans(1, fansSearch)}>
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <Card className="glass-card">
              <CardContent className="p-0">
                {fansLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : fans.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    Aucun fan trouvé
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium">Fan</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Email</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Téléphone</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Billets</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Inscrit le</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {fans.map((fan) => (
                          <tr key={fan.id} className="hover:bg-muted/30">
                            <td className="px-4 py-3">
                              <div className="font-medium">{fan.firstName} {fan.lastName}</div>
                            </td>
                            <td className="px-4 py-3 text-sm">{fan.email}</td>
                            <td className="px-4 py-3 text-sm">{fan.phone || '-'}</td>
                            <td className="px-4 py-3">
                              <Badge variant="secondary">{fan.ticketCount || 0}</Badge>
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">
                              {new Date(fan.createdAt).toLocaleDateString('fr-FR')}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <Button variant="ghost" size="sm" onClick={() => openFanDetail(fan)}>
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleResetFanPassword(fan.id)}>
                                  <Key className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDeleteFan(fan.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
              {fansTotalPages > 1 && (
                <div className="flex items-center justify-center gap-2 p-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={fansPage <= 1}
                    onClick={() => { setFansPage(fansPage - 1); fetchFans(fansPage - 1, fansSearch); }}
                  >
                    Précédent
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {fansPage} / {fansTotalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={fansPage >= fansTotalPages}
                    onClick={() => { setFansPage(fansPage + 1); fetchFans(fansPage + 1, fansSearch); }}
                  >
                    Suivant
                  </Button>
                </div>
              )}
            </Card>
          </motion.div>
        )}

        {/* Incidents Tab */}
        {activeTab === 'incidents' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
            data-testid="admin-incidents"
          >
            <div className="flex items-center justify-between">
              <div>
                <h1 className="font-syne text-3xl font-bold">Gestion des Incidents</h1>
                <p className="text-muted-foreground mt-1">
                  {incidentsStats.new > 0 && <span className="text-red-500 font-medium">{incidentsStats.new} nouveau(x)</span>}
                  {incidentsStats.new > 0 && incidentsStats.inProgress > 0 && ' • '}
                  {incidentsStats.inProgress > 0 && <span className="text-orange-500">{incidentsStats.inProgress} en cours</span>}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Select
                  value={incidentsFilter.status || "all"}
                  onValueChange={(value) => {
                    const newFilter = { ...incidentsFilter, status: value === "all" ? "" : value };
                    setIncidentsFilter(newFilter);
                    fetchIncidents(1, newFilter);
                  }}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Tous les statuts" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    <SelectItem value="new">Nouveau</SelectItem>
                    <SelectItem value="in_progress">En cours</SelectItem>
                    <SelectItem value="resolved">Résolu</SelectItem>
                    <SelectItem value="closed">Fermé</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={incidentsFilter.category || "all"}
                  onValueChange={(value) => {
                    const newFilter = { ...incidentsFilter, category: value === "all" ? "" : value };
                    setIncidentsFilter(newFilter);
                    fetchIncidents(1, newFilter);
                  }}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Toutes catégories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes catégories</SelectItem>
                    <SelectItem value="paiement">Paiement</SelectItem>
                    <SelectItem value="billets">Billets</SelectItem>
                    <SelectItem value="compte">Compte</SelectItem>
                    <SelectItem value="technique">Technique</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={() => fetchIncidents(incidentsPage)}>
                  <Filter className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <Card className="glass-card">
              <CardContent className="p-0">
                {incidentsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : incidents.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    Aucun incident trouvé
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium">Référence</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Utilisateur</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Sujet</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Catégorie</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Statut</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Priorité</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {incidents.map((incident) => (
                          <tr key={incident.id} className="hover:bg-muted/30">
                            <td className="px-4 py-3">
                              <span className="font-mono text-xs">#{incident.id.substring(0, 8).toUpperCase()}</span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm font-medium">{incident.name}</div>
                              <div className="text-xs text-muted-foreground">{incident.email}</div>
                            </td>
                            <td className="px-4 py-3 max-w-[200px] truncate text-sm">{incident.subject}</td>
                            <td className="px-4 py-3">
                              <Badge variant="outline" className="capitalize">{incident.category}</Badge>
                            </td>
                            <td className="px-4 py-3">
                              <Badge 
                                className={
                                  incident.status === 'new' ? 'bg-red-500 text-white' :
                                  incident.status === 'in_progress' ? 'bg-orange-500 text-white' :
                                  incident.status === 'resolved' ? 'bg-green-500 text-white' :
                                  'bg-gray-500 text-white'
                                }
                              >
                                {incident.status === 'new' ? 'Nouveau' :
                                 incident.status === 'in_progress' ? 'En cours' :
                                 incident.status === 'resolved' ? 'Résolu' : 'Fermé'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              <Badge 
                                variant="outline"
                                className={
                                  incident.priority === 'high' ? 'border-red-500 text-red-500' :
                                  incident.priority === 'medium' ? 'border-orange-500 text-orange-500' :
                                  'border-gray-500 text-gray-500'
                                }
                              >
                                {incident.priority === 'high' ? 'Haute' :
                                 incident.priority === 'medium' ? 'Moyenne' : 'Basse'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">
                              {new Date(incident.createdAt).toLocaleDateString('fr-FR')}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <Button variant="ghost" size="sm" onClick={() => openIncidentDetail(incident)}>
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {incident.status === 'new' && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => handleUpdateIncident(incident.id, { status: 'in_progress' })}
                                  >
                                    <Clock className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDeleteIncident(incident.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
              {incidentsTotalPages > 1 && (
                <div className="flex items-center justify-center gap-2 p-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={incidentsPage <= 1}
                    onClick={() => { setIncidentsPage(incidentsPage - 1); fetchIncidents(incidentsPage - 1); }}
                  >
                    Précédent
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {incidentsPage} / {incidentsTotalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={incidentsPage >= incidentsTotalPages}
                    onClick={() => { setIncidentsPage(incidentsPage + 1); fetchIncidents(incidentsPage + 1); }}
                  >
                    Suivant
                  </Button>
                </div>
              )}
            </Card>

            {/* Incident Detail Dialog */}
            <Dialog open={incidentDetailOpen} onOpenChange={setIncidentDetailOpen}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                {selectedIncident && (
                  <>
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <MessageCircleQuestion className="w-5 h-5 text-[#D4AF37]" />
                        Incident #{selectedIncident.id.substring(0, 8).toUpperCase()}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-muted-foreground">Utilisateur</Label>
                          <p className="font-medium">{selectedIncident.name}</p>
                          <p className="text-sm text-muted-foreground">{selectedIncident.email}</p>
                          {selectedIncident.phone && <p className="text-sm">{selectedIncident.phone}</p>}
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Type / Catégorie</Label>
                          <p className="font-medium capitalize">{selectedIncident.userType} - {selectedIncident.category}</p>
                        </div>
                      </div>

                      <div>
                        <Label className="text-muted-foreground">Sujet</Label>
                        <p className="font-medium">{selectedIncident.subject}</p>
                      </div>

                      <div>
                        <Label className="text-muted-foreground">Description</Label>
                        <p className="whitespace-pre-wrap bg-muted/50 p-3 rounded-md text-sm">{selectedIncident.description}</p>
                      </div>

                      {selectedIncident.attachmentUrl && (
                        <div>
                          <Label className="text-muted-foreground">Pièce jointe</Label>
                          <a 
                            href={`${process.env.REACT_APP_BACKEND_URL}${selectedIncident.attachmentUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline text-sm"
                          >
                            Voir la pièce jointe
                          </a>
                        </div>
                      )}

                      {selectedIncident.errorContext && (
                        <div>
                          <Label className="text-muted-foreground">Contexte d'erreur</Label>
                          <pre className="bg-destructive/10 p-3 rounded-md text-xs overflow-x-auto">
                            {JSON.stringify(selectedIncident.errorContext, null, 2)}
                          </pre>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-muted-foreground">Statut</Label>
                          <Select
                            value={selectedIncident.status}
                            onValueChange={(value) => handleUpdateIncident(selectedIncident.id, { status: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">Nouveau</SelectItem>
                              <SelectItem value="in_progress">En cours</SelectItem>
                              <SelectItem value="resolved">Résolu</SelectItem>
                              <SelectItem value="closed">Fermé</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Priorité</Label>
                          <Select
                            value={selectedIncident.priority}
                            onValueChange={(value) => handleUpdateIncident(selectedIncident.id, { priority: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">Basse</SelectItem>
                              <SelectItem value="medium">Moyenne</SelectItem>
                              <SelectItem value="high">Haute</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <Label className="text-muted-foreground">Notes admin</Label>
                        <Textarea
                          value={selectedIncident.adminNotes || ''}
                          onChange={(e) => setSelectedIncident(prev => ({ ...prev, adminNotes: e.target.value }))}
                          placeholder="Ajouter des notes..."
                          rows={3}
                        />
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="mt-2"
                          onClick={() => handleUpdateIncident(selectedIncident.id, { adminNotes: selectedIncident.adminNotes })}
                        >
                          Sauvegarder les notes
                        </Button>
                      </div>

                      <div className="text-xs text-muted-foreground">
                        <p>Page URL: {selectedIncident.pageUrl}</p>
                        <p>Créé le: {new Date(selectedIncident.createdAt).toLocaleString('fr-FR')}</p>
                        {selectedIncident.resolvedAt && (
                          <p>Résolu le: {new Date(selectedIncident.resolvedAt).toLocaleString('fr-FR')}</p>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </DialogContent>
            </Dialog>
          </motion.div>
        )}

        {/* Configuration Tab */}
        {activeTab === 'config' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
            data-testid="admin-config"
          >
            <div>
              <h1 className="font-syne text-3xl font-bold">Configuration de la plateforme</h1>
              <p className="text-muted-foreground mt-1">
                Gérez les commissions et paramètres de la plateforme
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {/* Commissions FCFA */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-600 text-xs font-bold">F</span>
                    Commissions FCFA
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Percent className="w-4 h-4" />
                      Commission plateforme (%)
                    </Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={platformConfig.commission_fcfa_percent}
                      onChange={(e) => setPlatformConfig(p => ({ ...p, commission_fcfa_percent: parseFloat(e.target.value) || 0 }))}
                      data-testid="config-fcfa-percent"
                    />
                    <p className="text-xs text-muted-foreground">
                      Exemple: Pour 5%, entrez 5. Commission actuelle sur les billets FCFA.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Numéro de reversement plateforme (ma part)
                    </Label>
                    <Input
                      type="tel"
                      placeholder="+221778312002"
                      value={platformConfig.platform_payment_number || ''}
                      onChange={(e) => setPlatformConfig(p => ({ ...p, platform_payment_number: e.target.value.trim() }))}
                      data-testid="config-platform-payment-number"
                    />
                    <p className="text-xs text-muted-foreground">
                      Numéro Wave / Orange Money qui reçoit ma part à chaque reversement artiste (complet ou partiel).
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Percent className="w-4 h-4" />
                        Frais Wave/OM (%)
                      </Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="10"
                        value={platformConfig.dkr_fee_percent ?? 1}
                        onChange={(e) => setPlatformConfig(p => ({ ...p, dkr_fee_percent: parseFloat(e.target.value) || 0 }))}
                        data-testid="config-dkr-fee-percent"
                      />
                      <p className="text-xs text-muted-foreground">Facturés au fan à l'achat.</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Percent className="w-4 h-4" />
                        Réserve plateforme (%)
                      </Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="5"
                        value={platformConfig.platform_reserve_percent ?? 1}
                        onChange={(e) => setPlatformConfig(p => ({ ...p, platform_reserve_percent: parseFloat(e.target.value) || 0 }))}
                        data-testid="config-platform-reserve-percent"
                      />
                      <p className="text-xs text-muted-foreground">Part des 5% conservée sur le compte plateforme.</p>
                    </div>
                  </div>
                  <div className="p-3 bg-green-500/10 rounded-lg text-xs space-y-1" data-testid="config-fcfa-example">
                    <p className="font-semibold">Exemple billet 10 000 F (commission {platformConfig.commission_fcfa_percent}%)</p>
                    <p>• Artiste supporte les frais : fan paie 10 100 F → artiste 9 500 F, ma part ≈ 300 F, frais ≈ 100 F, réserve 100 F</p>
                    <p>• Fan supporte les frais : fan paie 10 600 F → artiste 10 000 F, ma part ≈ 300 F, frais ≈ 100 F, réserve 100 F</p>
                  </div>
                </CardContent>
              </Card>

              {/* Nouveau modèle : Part artiste + Majoration + Délai transfert */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-600 text-xs font-bold">€</span>
                    Paiements EUR (Stripe)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-blue-500/10 rounded-lg mb-2">
                    <p className="text-sm">
                      <strong>Modèle de tarification :</strong> L'artiste reçoit X% du prix facial du billet. 
                      Une majoration fixe est ajoutée pour le client et va intégralement à la plateforme.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Percent className="w-4 h-4" />
                      Part artiste (%)
                    </Label>
                    <Input
                      type="number"
                      step="1"
                      min="80"
                      max="99"
                      value={platformConfig.artist_share_percent || 96}
                      onChange={(e) => setPlatformConfig(p => ({ ...p, artist_share_percent: parseFloat(e.target.value) || 96 }))}
                      data-testid="config-artist-share"
                    />
                    <p className="text-xs text-muted-foreground">
                      Exemple: 96% = artiste reçoit 96€ sur 100€ de prix facial
                    </p>
                  </div>
                  
                  {/* Majoration fixe par billet */}
                  <div className="p-4 bg-primary/10 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4" />
                        Majoration par billet (frais de service)
                      </Label>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Activé</span>
                        <input
                          type="checkbox"
                          checked={platformConfig.platform_fixed_fee_enabled !== false}
                          onChange={(e) => setPlatformConfig(p => ({ ...p, platform_fixed_fee_enabled: e.target.checked }))}
                          className="h-4 w-4"
                          data-testid="config-fixed-fee-enabled"
                        />
                      </div>
                    </div>
                    <Input
                      type="number"
                      step="0.10"
                      min="0"
                      max="5"
                      value={platformConfig.platform_fixed_fee_eur || 0.50}
                      onChange={(e) => setPlatformConfig(p => ({ ...p, platform_fixed_fee_eur: parseFloat(e.target.value) || 0 }))}
                      disabled={!platformConfig.platform_fixed_fee_enabled}
                      data-testid="config-fixed-fee-amount"
                    />
                    <p className="text-xs text-muted-foreground">
                      Montant en € ajouté par billet (ex: 0,50€). Affiché comme "Frais de service" au client. 100% pour la plateforme.
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Délai de transfert (jours)
                    </Label>
                    <Input
                      type="number"
                      step="1"
                      min="1"
                      max="30"
                      value={platformConfig.transfer_delay_days || 7}
                      onChange={(e) => setPlatformConfig(p => ({ ...p, transfer_delay_days: parseInt(e.target.value) || 7 }))}
                      data-testid="config-transfer-delay"
                    />
                    <p className="text-xs text-muted-foreground">
                      Nombre de jours avant de transférer les fonds à l'artiste (temps pour que les fonds soient disponibles)
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Transferts en attente */}
              <Card className="glass-card md:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-primary" />
                    Transferts en attente
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-green-500/10 rounded-lg">
                      <p className="text-sm text-muted-foreground">Prêts à transférer</p>
                      <p className="text-2xl font-bold text-green-500">{pendingTransfers.ready_count} ({pendingTransfers.ready_total?.toFixed(2) || 0}€)</p>
                    </div>
                    <div className="p-4 bg-yellow-500/10 rounded-lg">
                      <p className="text-sm text-muted-foreground">En attente (date future)</p>
                      <p className="text-2xl font-bold text-yellow-500">{pendingTransfers.future_count} ({pendingTransfers.future_total?.toFixed(2) || 0}€)</p>
                    </div>
                  </div>
                  
                  {pendingTransfers.ready_count > 0 && (
                    <Button 
                      onClick={handleExecuteTransfers} 
                      disabled={executingTransfers}
                      className="w-full btn-primary"
                      data-testid="execute-transfers-btn"
                    >
                      {executingTransfers ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Transferts en cours...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Exécuter {pendingTransfers.ready_count} transfert(s) ({pendingTransfers.ready_total?.toFixed(2)}€)
                        </>
                      )}
                    </Button>
                  )}
                  
                  <Button 
                    variant="outline" 
                    onClick={fetchPendingTransfers}
                    className="w-full"
                  >
                    Rafraîchir
                  </Button>
                </CardContent>
              </Card>

              {/* Commission Apporteurs */}
              <Card className="glass-card md:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-primary" />
                    Commission Apporteurs (Parrainage)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-muted/50 rounded-lg mb-4">
                    <p className="text-sm">
                      <strong>Fonctionnement :</strong> La commission apporteur est déduite de VOTRE marge plateforme, pas de celle de l'artiste.
                      <br />
                      Commission sur FCFA uniquement. Pas de commission sur EUR.
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Percent className="w-4 h-4 text-green-500" />
                        Part apporteur FCFA (%)
                      </Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="5"
                        value={platformConfig.apporteur_share_percent_fcfa ?? platformConfig.apporteur_share_percent ?? 1}
                        onChange={(e) => setPlatformConfig(p => ({ ...p, apporteur_share_percent_fcfa: parseFloat(e.target.value) || 0, apporteur_share_percent: parseFloat(e.target.value) || 0 }))}
                        data-testid="config-apporteur-percent-fcfa"
                      />
                      <p className="text-xs text-muted-foreground">
                        Commission sur ventes FCFA
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Percent className="w-4 h-4 text-blue-500" />
                        Part apporteur EUR (%)
                      </Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="5"
                        value={platformConfig.apporteur_share_percent_eur ?? 0}
                        onChange={(e) => setPlatformConfig(p => ({ ...p, apporteur_share_percent_eur: parseFloat(e.target.value) || 0 }))}
                        data-testid="config-apporteur-percent-eur"
                      />
                      <p className="text-xs text-muted-foreground">
                        Commission sur ventes EUR
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Ticket className="w-4 h-4" />
                        Limite d'événements
                      </Label>
                      <Input
                        type="number"
                        step="1"
                        min="1"
                        max="100"
                        value={platformConfig.apporteur_events_limit}
                        onChange={(e) => setPlatformConfig(p => ({ ...p, apporteur_events_limit: parseInt(e.target.value) || 5 }))}
                        data-testid="config-apporteur-limit"
                      />
                      <p className="text-xs text-muted-foreground">
                        Sur les X premiers événements
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Assistant IA */}
              <Card className="glass-card md:col-span-2" data-testid="config-ai-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-600 text-xs font-bold">IA</span>
                    Assistant de rédaction IA (Gemini)
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-4 bg-muted/40 rounded-lg">
                    <div>
                      <p className="font-medium">Assistant IA activé</p>
                      <p className="text-xs text-muted-foreground">Si désactivé, les boutons « Générer / Améliorer » disparaissent pour tous.</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={platformConfig.ai_enabled !== false}
                      onChange={(e) => setPlatformConfig(p => ({ ...p, ai_enabled: e.target.checked }))}
                      className="h-5 w-5"
                      data-testid="config-ai-enabled"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Quota par utilisateur et par jour</Label>
                    <Input
                      type="number"
                      min="0"
                      max="500"
                      value={platformConfig.ai_daily_quota ?? 20}
                      onChange={(e) => setPlatformConfig(p => ({ ...p, ai_daily_quota: parseInt(e.target.value, 10) || 0 }))}
                      data-testid="config-ai-daily-quota"
                    />
                    <p className="text-xs text-muted-foreground">Générations + améliorations confondues (défaut : 20).</p>
                  </div>
                </CardContent>
              </Card>

              {/* Résumé des commissions */}
              <Card className="glass-card md:col-span-2 bg-gradient-to-r from-primary/10 to-transparent">
                <CardHeader>
                  <CardTitle>Résumé de la tarification actuelle</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                    <div className="p-4 bg-background/50 rounded-lg">
                      <p className="text-2xl font-bold text-green-500">{platformConfig.commission_fcfa_percent}%</p>
                      <p className="text-xs text-muted-foreground">Commission FCFA</p>
                    </div>
                    <div className="p-4 bg-background/50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-500">{platformConfig.artist_share_percent || 96}%</p>
                      <p className="text-xs text-muted-foreground">Part artiste EUR</p>
                    </div>
                    <div className="p-4 bg-background/50 rounded-lg">
                      <p className="text-2xl font-bold text-orange-500">
                        {platformConfig.platform_fixed_fee_enabled !== false 
                          ? `${platformConfig.platform_fixed_fee_eur || 0.50}€` 
                          : 'Désactivé'}
                      </p>
                      <p className="text-xs text-muted-foreground">Frais de service/billet</p>
                    </div>
                    <div className="p-4 bg-background/50 rounded-lg">
                      <p className="text-2xl font-bold text-primary">
                        {platformConfig.apporteur_share_percent_fcfa ?? platformConfig.apporteur_share_percent ?? 1}% FCFA
                      </p>
                      <p className="text-xs text-muted-foreground">Part apporteur (0% EUR)</p>
                    </div>
                    <div className="p-4 bg-background/50 rounded-lg">
                      <p className="text-2xl font-bold">{platformConfig.apporteur_events_limit}</p>
                      <p className="text-xs text-muted-foreground">Événements éligibles</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button 
                onClick={handleUpdateConfig} 
                className="btn-primary"
                disabled={savingConfig}
                data-testid="save-config-btn"
              >
                {savingConfig ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Settings className="w-4 h-4 mr-2" />
                    Enregistrer la configuration
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        )}
      </main>

      {/* Apporteur Detail Dialog */}
      <Dialog open={apporteurDetailDialogOpen} onOpenChange={setApporteurDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Détail de l'apporteur</DialogTitle>
          </DialogHeader>
          {selectedApporteur && (
            <div className="space-y-6">
              {/* Info apporteur */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Nom complet</p>
                  <p className="font-bold">{selectedApporteur.firstName} {selectedApporteur.lastName}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{selectedApporteur.email || 'Non renseigné'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Téléphone Wave</p>
                  <p className="font-medium">{selectedApporteur.phoneWave || 'Non renseigné'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Téléphone Orange Money</p>
                  <p className="font-medium">{selectedApporteur.phoneOrangeMoney || 'Non renseigné'}</p>
                </div>
                {selectedApporteur.ibanMasked && (
                  <div>
                    <p className="text-sm text-muted-foreground">IBAN</p>
                    <p className="font-mono">{selectedApporteur.ibanMasked}</p>
                  </div>
                )}
                {selectedApporteur.bicMasked && (
                  <div>
                    <p className="text-sm text-muted-foreground">BIC</p>
                    <p className="font-mono">{selectedApporteur.bicMasked}</p>
                  </div>
                )}
              </div>
              
              {/* Code et QR */}
              <div className="p-4 bg-primary/10 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Code parrainage</p>
                    <p className="font-mono text-2xl font-bold text-primary">{selectedApporteur.code}</p>
                  </div>
                  {selectedApporteur.qrCodeUrl && (
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}${selectedApporteur.qrCodeUrl}`}
                      alt="QR Code"
                      className="w-24 h-24 border border-border rounded-lg bg-white"
                    />
                  )}
                </div>
                {selectedApporteur.onboardingUrl && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Lien d'inscription :</span>
                    <a 
                      href={selectedApporteur.onboardingUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline flex items-center gap-1"
                    >
                      {selectedApporteur.onboardingUrl.substring(0, 50)}...
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </div>
              
              {/* Artistes parrainés */}
              <div>
                <h4 className="font-bold mb-3">Artistes parrainés ({selectedApporteur.artistsCount || 0})</h4>
                {(!selectedApporteur.parrainedArtists || selectedApporteur.parrainedArtists.length === 0) ? (
                  <p className="text-muted-foreground text-sm">Aucun artiste parrainé pour le moment</p>
                ) : (
                  <div className="space-y-2">
                    {selectedApporteur.parrainedArtists.map(artist => (
                      <div key={artist.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div>
                          <p className="font-medium">{artist.nomDeScene}</p>
                          <p className="text-xs text-muted-foreground">
                            Parrainé le {new Date(artist.apporteurAttributionAt).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${artist.isActive ? 'bg-green-500/20 text-green-600' : 'bg-gray-500/20 text-gray-600'}`}>
                          {artist.isActive ? 'Actif' : 'Inactif'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Info mot de passe par défaut si nouvellement créé */}
              {selectedApporteur.defaultPassword && (
                <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <p className="text-sm font-medium text-yellow-600 mb-1">Mot de passe par défaut</p>
                  <p className="font-mono font-bold">{selectedApporteur.defaultPassword}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Ce mot de passe ne sera affiché qu'une seule fois. L'apporteur devra le changer à la première connexion.
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Credentials Dialog */}
      <Dialog open={credentialsDialogOpen} onOpenChange={setCredentialsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Identifiants créés</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Voici les identifiants créés pour l'artiste. Notez-les car ils ne seront plus affichés.
            </p>
            {createdCredentials?.map((cred, index) => (
              <Card key={index} className="bg-muted">
                <CardContent className="p-4">
                  <p className="font-medium">{cred.role}</p>
                  <p className="text-sm">Email: <span className="font-mono">{cred.email}</span></p>
                  <p className="text-sm">Mot de passe: <span className="font-mono">{cred.password}</span></p>
                </CardContent>
              </Card>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setCredentialsDialogOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Artist Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <AlertTriangle className="w-5 h-5" />
              Supprimer l'artiste
            </DialogTitle>
          </DialogHeader>
          {selectedArtistForAction && (
            <div className="space-y-4">
              <p className="text-sm">
                Êtes-vous sûr de vouloir supprimer l'artiste <strong>{selectedArtistForAction.nomDeScene}</strong> ?
              </p>
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-600 font-medium">⚠️ Cette action est irréversible !</p>
                <p className="text-xs text-red-500 mt-2">
                  Toutes les données seront supprimées :
                </p>
                <ul className="text-xs text-red-500 mt-1 list-disc list-inside">
                  <li>Événements</li>
                  <li>Billets vendus</li>
                  <li>Posts et médias</li>
                  <li>Messages</li>
                  <li>Comptes utilisateurs (artiste + managers)</li>
                </ul>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} disabled={actionLoading}>
                  Annuler
                </Button>
                <Button 
                  className="bg-red-500 hover:bg-red-600 text-white"
                  onClick={handleDeleteArtist}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Suppression...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Supprimer définitivement
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordOpen} onOpenChange={(open) => {
        setResetPasswordOpen(open);
        if (!open) {
          setGeneratedPassword(null);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-primary" />
              Réinitialiser le mot de passe
            </DialogTitle>
          </DialogHeader>
          {selectedArtistForAction && (
            <div className="space-y-4">
              {!generatedPassword ? (
                <>
                  <p className="text-sm">
                    Réinitialiser le mot de passe de <strong>{selectedArtistForAction.nomDeScene}</strong> ?
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Un nouveau mot de passe sera généré. L'artiste devra le changer à sa prochaine connexion.
                  </p>
                  <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={() => setResetPasswordOpen(false)} disabled={actionLoading}>
                      Annuler
                    </Button>
                    <Button 
                      className="btn-primary"
                      onClick={handleResetPassword}
                      disabled={actionLoading}
                    >
                      {actionLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Génération...
                        </>
                      ) : (
                        <>
                          <Key className="w-4 h-4 mr-2" />
                          Générer un nouveau mot de passe
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </>
              ) : (
                <>
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-sm text-green-600 font-medium mb-3">✅ Mot de passe réinitialisé !</p>
                    
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-muted-foreground">Email :</p>
                        <div className="flex items-center gap-2">
                          <p className="font-mono text-sm">{generatedPassword.email}</p>
                          <Button variant="ghost" size="sm" onClick={() => copyToClipboard(generatedPassword.email)}>
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-xs text-muted-foreground">Nouveau mot de passe :</p>
                        <div className="flex items-center gap-2">
                          <p className="font-mono text-lg font-bold text-primary">{generatedPassword.password}</p>
                          <Button variant="ghost" size="sm" onClick={() => copyToClipboard(generatedPassword.password)}>
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-xs text-muted-foreground">
                    ⚠️ Notez ce mot de passe et transmettez-le à l'artiste. Il ne sera plus affiché.
                  </p>
                  
                  <DialogFooter>
                    <Button onClick={() => {
                      setResetPasswordOpen(false);
                      setGeneratedPassword(null);
                    }}>
                      Fermer
                    </Button>
                  </DialogFooter>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
